/**
 * Product catalogue.
 *
 * The rate shown is the same for every customer — variation comes only from
 * the customer's discount percentage, which the API applies and returns
 * separately. That is stated on screen, because a retailer who suspects
 * per-customer pricing stops trusting the app.
 *
 * Cached for offline use: a shop with no signal can still browse and build a
 * cart, and the order queues until reconnect.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page } from '../../components/Shell';
import {
  Banner, Button, Card, Chip, ChipRow, Empty, ErrorState, Money, Skeleton, Stepper, useToast,
} from '../../components/ui';
import { useQuery, useDebounced, useRefreshOnFocus } from '../../lib/useApi';
import { useCart, lineKey } from '../../lib/cart';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { api, mediaUrl } from '../../lib/api';
import { IconSearch, IconCart } from '../../components/Icons';
import { dateShort, piecesAndPacks } from '../../lib/format';

interface CatalogueItem {
  id: string;
  skuCode: string;
  name: { en: string; hi: string | null };
  packSize: string | null;
  uom: string;
  /**
   * Business Central's units. `packQty` is pieces per box — 24 for a 1X24 —
   * and null means BC lists no unit larger than the base, which is a real
   * answer for something sold only loose and must never be read as 1.
   */
  baseUom: string | null;
  packUom: string | null;
  packQty: number | null;
  brand: string | null;
  category: string | null;
  categoryId: string | null;
  imageUrl: string | null;
  isReturnable: boolean;
  pricing: {
    mrpPaise: number;
    ratePaise: number;
    discountPercent: number;
    discountPaise: number;
    netRatePaise: number;
    landedPerUnitPaise: number;
    display: { mrp: string; rate: string; net: string };
    /**
     * The box price, when the item has a pack AND somebody has priced it.
     *
     * Null when either is missing — the server refuses to guess a box price,
     * and the catalogue must not present one it invented.
     */
    box: {
      ratePaise: number;
      packQty: number;
      perPiecePaise: number;
      display: { rate: string; perPiece: string };
    } | null;
  };
  // `availability` is gone. Business Central owns stock, the app reads a
  // figure up to two minutes old, and a stale "out of stock" costs a sale
  // while a stale "in stock" costs an argument. Admin sees the real numbers,
  // with the age of the reading, on the Stock screen.
}

interface CatalogueResponse {
  items: CatalogueItem[];
  count: number;
  appliedDiscountPercent: number;
  note: string;
}

interface RepeatOrder {
  available: boolean;
  sourceOrder?: { orderNumber: string; orderDate: string };
  lines?: { productId: string; skuCode: string; productName: string; uom: string; quantity: number }[];
  unavailableItems?: { skuCode: string; productName: string }[];
}

export default function Catalogue() {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const cart = useCart();

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('all');
  const debouncedSearch = useDebounced(search, 300);

  /**
   * Whose catalogue this is.
   *
   * A customer's own id when they are ordering for themselves; otherwise the
   * customer the staff member is ordering on behalf of, held in the cart.
   *
   * This used to be `user?.customerId` alone, which is null for every staff
   * role. The consequence was quiet and total: the catalogue loaded without a
   * customer, so no customer-specific pricing, no repeat-order suggestion, and
   * downstream in the cart no shops and a permanently disabled place button.
   * The salesperson "Book Order" flow appeared to work right up to the last
   * tap.
   */
  const customerId = user?.customerId ?? cart.customerId;

  const { data, loading, error, stale, refetch } = useQuery<CatalogueResponse>(
    customerId ? `/api/products/catalogue?customerId=${customerId}` : '/api/products/catalogue',
    // Cached for a day: prices are effective-dated server-side, so a stale
    // catalogue shows the right rate for its date and the order re-prices on
    // submit anyway.
    { cacheKey: `catalogue:${customerId ?? 'all'}`, ttlSeconds: 86400 },
  );
  useRefreshOnFocus(refetch);

  const { data: repeat } = useQuery<RepeatOrder>(
    customerId && cart.shopId
      ? `/api/products/repeat-order?customerId=${customerId}&customerShopId=${cart.shopId}`
      : null,
  );

  const categories = useMemo(() => {
    if (!data) return [];
    const seen = new Map<string, string>();
    for (const item of data.items) {
      if (item.categoryId && item.category) seen.set(item.categoryId, item.category);
    }
    return [{ value: 'all', label: t('app.total') }, ...Array.from(seen, ([value, label]) => ({ value, label }))];
  }, [data, t]);

  const visible = useMemo(() => {
    if (!data) return [];
    const q = debouncedSearch.trim().toLowerCase();
    return data.items.filter((item) => {
      if (category !== 'all' && item.categoryId !== category) return false;
      if (!q) return true;
      return item.name.en.toLowerCase().includes(q)
        || (item.name.hi?.includes(debouncedSearch) ?? false)
        || item.skuCode.toLowerCase().includes(q);
    });
  }, [data, debouncedSearch, category]);

  async function applyRepeatOrder() {
    if (!repeat?.lines?.length || !data) return;
    const byId = new Map(data.items.map((i) => [i.id, i]));
    const lines = repeat.lines
      .map((l) => {
        const product = byId.get(l.productId);
        if (!product) return null;
        return {
          productId: product.id,
          skuCode: product.skuCode,
          productName: product.name.en,
          uom: product.uom,
          quantity: l.quantity,
          ratePaise: product.pricing.ratePaise,
          imageUrl: product.imageUrl,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);

    cart.replaceAll(lines);
    toast.show(`${lines.length} items added`, 'ok');
    navigate('/cart');
  }

  return (
    <Page
      title={t('catalogue.title')}
      badges={{ cart: cart.totalQuantity }}
      actions={
        cart.count > 0 ? (
          <Button size="sm" icon={<IconCart width={16} height={16} />} onClick={() => navigate('/cart')}>
            {cart.totalQuantity}
          </Button>
        ) : undefined
      }
    >
      <div className="stack">
        {stale && <Banner tone="warn">{t('app.staleData')}</Banner>}

        <div className="field">
          <div style={{ position: 'relative' }}>
            <IconSearch
              width={18} height={18}
              style={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--faint)', pointerEvents: 'none',
              }}
            />
            <input
              className="input"
              style={{ paddingLeft: 38 }}
              placeholder={t('app.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputMode="search"
              aria-label={t('app.search')}
            />
          </div>
        </div>

        {categories.length > 2 && (
          <ChipRow value={category} onChange={setCategory} options={categories} wrap />
        )}

        {/* One tap to rebuild last week's basket. For a kirana buying the same
            twelve SKUs every week, this is the difference between using the app
            and phoning the order in. */}
        {repeat?.available && repeat.sourceOrder && cart.count === 0 && (
          <Card>
            <div className="row-between">
              <div className="grow">
                <div className="t-semi">{t('catalogue.repeatLast')}</div>
                <div className="t-xs t-muted">
                  {t('catalogue.repeatLastHint', {
                    number: repeat.sourceOrder.orderNumber,
                    date: dateShort(repeat.sourceOrder.orderDate),
                  })}
                </div>
                {repeat.unavailableItems && repeat.unavailableItems.length > 0 && (
                  <div className="t-xs t-warn" style={{ marginTop: 4 }}>
                    {repeat.unavailableItems.length} item(s) no longer available
                  </div>
                )}
              </div>
              <Button size="sm" onClick={() => void applyRepeatOrder()}>{t('catalogue.addToCart')}</Button>
            </div>
          </Card>
        )}

        {data && data.appliedDiscountPercent > 0 && (
          <Banner tone="ok">
            <span className="t-sm">
              {data.appliedDiscountPercent}% discount applied · {t('catalogue.sameRateNote')}
            </span>
          </Banner>
        )}

        {loading && !data && <Skeleton rows={5} height={92} />}
        {error && !data && <ErrorState message={error.message} onRetry={refetch} />}

        {data && visible.length === 0 && (
          <Empty
            title={search ? 'No matching products' : t('app.noData')}
            hint={search ? 'Try a different search' : undefined}
          />
        )}

        <div className="stack-2">
          {visible.map((item) => (
            <ProductRow key={item.id} item={item} />
          ))}
        </div>
      </div>

      {cart.count > 0 && (
        <div className="actionbar">
          <div className="grow stack-2" style={{ gap: 0, justifyContent: 'center' }}>
            <span className="t-xs t-muted">{cart.totalQuantity} {t('cart.quantity')}</span>
            <Money paise={cart.estimatedGrossPaise} />
          </div>
          <Button onClick={() => navigate('/cart')}>{t('nav.cart')}</Button>
        </div>
      )}
    </Page>
  );
}

function ProductRow({ item }: { item: CatalogueItem }) {
  const t = useT();
  const cart = useCart();
  const inCartPcs = cart.quantityOf(item.id, 'PCS');
  const inCartBox = cart.quantityOf(item.id, 'BOX');

  // A pack of 1 is not a pack; the same rule the server applies.
  const hasBox = !!item.packQty && item.packQty > 1;

  /** Everything a basket line needs except the quantity and the unit. */
  const cartLine = {
    productId: item.id,
    skuCode: item.skuCode,
    productName: item.name.en,
    uom: item.uom,
    packUom: item.packUom,
    packQty: item.packQty,
    ratePaise: item.pricing.ratePaise,
    boxPricePaise: item.pricing.box?.ratePaise ?? null,
    imageUrl: item.imageUrl,
  };

  /**
   * No stock chip, and no disabled Add button.
   *
   * Business Central owns inventory, and the app reads a figure that is up to
   * two minutes old. Telling a retailer "out of stock" from a stale read makes
   * them not order something that is on the shelf; the reverse makes them
   * argue about a number the app is not authoritative for.
   *
   * So retailers order what they want. The godown decides what can actually be
   * dispatched, and a shortage is settled at delivery where it becomes a
   * credit note rather than a lost sale.
   */

  return (
    <Card>
      {/*
        MOBILE LAYOUT: image + name/price on top, the quantity steppers on their
        own full-width row below — reported Point 9.

        The two steppers are 136px each. Kept beside the name they squeezed the
        product name to zero width and ran the pieces stepper off the right edge
        of a phone. On their own row the name always has the full width and
        neither stepper can overflow; they wrap to a second line on the very
        narrowest screens rather than disappearing off the edge.
      */}
      <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
        {item.imageUrl ? (
          <img
            src={mediaUrl(item.imageUrl)}
            alt=""
            className="thumb"
            loading="lazy"
            // Product images come from Business Central, mirrored into R2. A
            // missing image must not leave a broken-image glyph.
            onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
          />
        ) : (
          <div className="thumb" aria-hidden />
        )}

        <div className="grow" style={{ minWidth: 0 }}>
          <div className="t-semi t-2line">{item.name.en}</div>
          {item.name.hi && <div className="t-xs t-muted t-clip">{item.name.hi}</div>}

          <div className="row-wrap" style={{ gap: 6, marginTop: 6 }}>
            {item.packSize && <Chip>{item.packSize}</Chip>}
            {item.brand && <Chip outline>{item.brand}</Chip>}
            {item.isReturnable && <Chip outline>Returnable</Chip>}
          </div>

          <div className="row" style={{ gap: 10, marginTop: 8, alignItems: 'baseline' }}>
            <Money paise={item.pricing.ratePaise} className="t-lg" />
            <span className="t-xs t-muted">/ {item.uom.toLowerCase()}</span>
            {item.pricing.mrpPaise > item.pricing.ratePaise && (
              <span className="t-xs t-faint" style={{ textDecoration: 'line-through' }}>
                {item.pricing.display.mrp}
              </span>
            )}
          </div>
          {item.pricing.discountPercent > 0 && (
            <div className="t-xs t-ok">
              −{item.pricing.discountPercent}% · {t('cart.taxable')} {item.pricing.display.net} incl. tax
            </div>
          )}
        </div>
      </div>

      {/*
        QUANTITY — boxes on the left, pieces on the right, both from zero. These
        are two independent lines, never a unit toggle: the same "2" meaning two
        bottles or forty-eight is a 24x dispute waiting at a shop door, so what
        each number counts is written above it and never changes. A product with
        no pack size gets the pieces field only.

        The column labels are red and bold, per the client.
      */}
      <div
        className="row-wrap"
        style={{ gap: 16, marginTop: 12, alignItems: 'flex-start' }}
      >
        {hasBox && (
          <div className="stack-1" style={{ gap: 4, alignItems: 'center', flex: '0 0 auto' }}>
            <span className="t-xs t-danger t-bold">Pets ({item.packQty})</span>
            <Stepper
              value={inCartBox}
              onChange={(n) => (inCartBox === 0 && n > 0
                ? cart.add({ ...cartLine, soldUom: 'BOX' }, n)
                : cart.setQuantity(lineKey(item.id, 'BOX'), n))}
              min={0}
              max={9999}
            />
            {inCartBox > 0 && (
              <span className="t-xs t-muted">{inCartBox * (item.packQty ?? 0)} Pieces</span>
            )}
          </div>
        )}

        <div className="stack-1" style={{ gap: 4, alignItems: 'center', flex: '0 0 auto' }}>
          <span className="t-xs t-danger t-bold">Pieces</span>
          <Stepper
            value={inCartPcs}
            onChange={(n) => (inCartPcs === 0 && n > 0
              ? cart.add({ ...cartLine, soldUom: 'PCS' }, n)
              : cart.setQuantity(lineKey(item.id, 'PCS'), n))}
            min={0}
            max={9999}
          />
        </div>
      </div>
    </Card>
  );
}
